import { useEffect, useMemo, useState } from 'react'
import { productGA } from '@/helpers/GA4Events'

import { customerGA } from '@/helpers/customerGA'

import { formatMoney } from '@/helpers/formatMoney'

import { useProduct } from '@/hooks/useProduct/index'
import { usePrevious } from '@/hooks/usePrevious'

import { GraphQlCustomer } from '@/helpers/graphql'
import { error, log } from '@/services/Log'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'

import { bloomreachPageViewPixel } from '@/components/ScriptIntegrations/BloomreachEvents'
import type { Product } from '@/types/SearchAPI/SAPIProduct'
import listrakProductBrowse from '@/services/Listrak/listrakProductBrowse'
import MarketingChannels from '@/services/MarketingChannels'
import { Facebook } from '@/services/Facebook'

interface PdpTrackingServicesProps {
  user: GraphQlCustomer | null,
  cart: StandardCart | null
  product: Product
}

const PdpTrackingServices: React.FC<PdpTrackingServicesProps> = ({
  user,
  cart,
  product,
}) => {
  const {
    sku,
    price,
    calculatedPrice: fallBackPrice,
    variantId: selectedVariantId,
    selectedVariant,
  } = useProduct()

  const variantPrice = selectedVariant?.calculated_price
  const calculatedPrice = formatMoney(variantPrice) || fallBackPrice || 'Not available'
  const previousSku = usePrevious(sku)
  useEffect(() => {
    if (sku && !previousSku) {
      customerGA(cart?.total || 0, 'product')
    }
  }, [cart, sku, previousSku])

  useEffect(() => {
    bloomreachPageViewPixel(product, user?.entityId, selectedVariant?.sku)
  }, [selectedVariant, product, user])

  const cartTotal = useMemo(() => cart?.total || 0, [cart])
  const quantity = useMemo(() => (
    [
      ...(cart?.lineItems || []),
    ]?.find((i) => i?.sku === sku)?.quantity || 0
  ), [cart, sku])
  const customerId = user?.entityId
  const customerEmail = user?.email
  const salePrice = useMemo(() => parseFloat(`${calculatedPrice}`.replace(/(^\$|,)/g, '')), [calculatedPrice])
  const [, setDebouncer] = useState<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (!sku || !selectedVariantId || !product) {
      return
    }
    setDebouncer((prev) => {
      if (prev) {
        clearTimeout(prev)
      }
      return setTimeout(() => {
        setDebouncer(null)
        const v = {
          name: product.name,
          sku,
          price,
          salePrice,
          variantId: selectedVariantId,
          categories: product.category_info,
          event: 'productDetailView',
          brand: product.brand || '',
          quantity,
          customerId,
          customerEmail,
          cartTotal,
        } as const
        log('hooks/PdpTrackingServices.tsx', v)
        productGA(v)

        MarketingChannels.onSkuChange(
          product.name,
          product.category_info,
          selectedVariantId,
          salePrice,
        )
        listrakProductBrowse(sku)
      }, 1000)
    })
  }, [
    sku,
    salePrice,
    selectedVariantId,
    product,
    quantity,
    cartTotal,
    customerId,
    customerEmail,
    price,
  ])

  useEffect(() => {
    const reportViewContentToFacebook = async () => {
      if (!sku || !selectedVariant || !product) {
        return
      }
      const salePrice = parseFloat(`${calculatedPrice}`.replace(/(^\$|,)/g, ''))
      Facebook.viewContent({
        user,
        content_ids: [sku],
        content_category: [...product.category_info].pop()?.name || '',
        content_name: selectedVariant.name,
        contents: [{ id: sku, quantity: 1, item_price: salePrice }],
        value: salePrice,
      }).catch(error)
    }
    reportViewContentToFacebook().catch(error)
  }, [sku, selectedVariant, calculatedPrice, product, user])
  return null
}

export default PdpTrackingServices
